import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import ConfirmDialog from '../../../shared/components/confirm-dialog/ConfirmDialog';
import BackButton from '../../../shared/components/back-button/BackButton';
import ServiceBasicsEditorPage from './ServiceBasicsEditorPage';
import { fetchCompanyStaff } from '../../../shared/booking/service-staff/serviceStaffRouting';
import { deleteService, listServices, upsertService } from '../../../api/companyRecords';
import useCompanyLocations from '../../../locations/hooks/useCompanyLocations';
import { normalizeLocationIds } from '../../../locations/locationTypes';
import useResolvedServiceRoute from '../hooks/useResolvedServiceRoute';
import { buildServiceSlugFields, getServicePathSegment } from '../utils/serviceSlug';
import '../styles/services.css';
import './service-details-v2.css';

export default function ServiceEditPage() {
  const { serviceId: routeParam } = useParams();
  const navigate = useNavigate();
  const { companyData } = useAuth();
  const { setNotification } = useNotification();
  const { activeLocations } = useCompanyLocations({
    companyId: companyData?.id,
    companyData,
    activeOnly: true,
  });
  const { status, service: currentService, firestoreId } = useResolvedServiceRoute(routeParam);

  const [staffOptions, setStaffOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    const loadStaffOptions = async () => {
      if (!companyData?.id) {
        setStaffOptions([]);
        return;
      }
      try {
        const nextStaffOptions = await fetchCompanyStaff({
          companyId: companyData.id,
          ownerId: companyData?.ownerId || companyData?.createdBy,
        });
        setStaffOptions(nextStaffOptions);
      } catch (error) {
        console.error('Error loading staff options:', error);
      }
    };
    loadStaffOptions();
  }, [companyData?.id, companyData?.ownerId, companyData?.createdBy]);

  const backToServiceDetails = () => {
    const path = getServicePathSegment(currentService) || routeParam;
    navigate(`/app/overview/services/${path}`);
  };

  const handleSaveBasics = async (basics) => {
    if (!firestoreId || !companyData?.id) return;
    setSaving(true);
    try {
      const services = (await listServices({ companyId: companyData.id }))
        .filter((entry) => !entry._init);

      const slugFields = buildServiceSlugFields(basics.name, services, firestoreId);
      const payload = {
        name: basics.name,
        description: basics.description || '',
        duration: basics.duration,
        bufferTime: basics.bufferTime ?? 0,
        price: basics.price,
        imageUrl: basics.imageUrl || '',
        employeeIds: Array.isArray(basics.employeeIds) ? basics.employeeIds : [],
        locationIds: normalizeLocationIds(basics.locationIds).length > 0
          ? normalizeLocationIds(basics.locationIds)
          : activeLocations.map((location) => location.id),
        ...slugFields,
        updatedAt: new Date().toISOString(),
        calendarColor: basics.calendarColor || null,
      };
      await upsertService({
        companyId: companyData.id,
        id: firestoreId,
        service: payload,
      });
      setNotification({ open: true, message: 'Service updated successfully', severity: 'success' });
      navigate(`/app/overview/services/${slugFields.slug}`);
    } catch (error) {
      console.error('Error saving service:', error);
      setNotification({
        open: true,
        message: `Error saving service: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async () => {
    if (!firestoreId || !companyData?.id) return;
    setSaving(true);
    try {
      await deleteService({ companyId: companyData.id, id: firestoreId });
      setConfirmDeleteOpen(false);
      setNotification({ open: true, message: 'Service deleted successfully', severity: 'success' });
      navigate('/app/overview/settings', {
        state: { section: 'services-categories', parent: 'services-categories', servicesTab: 'services' },
      });
    } catch (error) {
      console.error('Error deleting service:', error);
      setNotification({
        open: true,
        message: `Error deleting service: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (status === 'loading' || status === 'idle') return null;
  if (status === 'not_found' || !currentService) return null;

  return (
    <div className="svc-page sd2-editPage">
      <div className="sd2-editTopbar">
        <BackButton label="Back to service" className="back-btn--flush" onClick={backToServiceDetails} />
      </div>

      <ServiceBasicsEditorPage
        initialService={currentService}
        companyId={companyData?.id}
        busy={saving}
        staffOptions={staffOptions}
        locationOptions={activeLocations}
        onCancel={backToServiceDetails}
        onSave={handleSaveBasics}
        onDelete={() => setConfirmDeleteOpen(true)}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete service?"
        message="This will permanently delete this service. This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        busy={saving}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteService}
      />
    </div>
  );
}
